import { gtag } from 'gtag';

declare global {
  interface Window {
    gtag: any;
    dataLayer: any[];
  }
}

export const GA_TRACKING_ID = import.meta.env.VITE_GA_TRACKING_ID || 'G-XXXXXXXXXX';

let analyticsConsent = false;

export const setAnalyticsConsent = (hasConsent: boolean) => {
  analyticsConsent = hasConsent;
  
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: hasConsent ? 'granted' : 'denied',
    });
  }
};

export const initGA = (hasAnalyticsConsent: boolean = false) => {
  // Only activate in production (uncomment line below to enable)
  // if (import.meta.env.DEV) return;
  
  if (typeof window === 'undefined' || !GA_TRACKING_ID || GA_TRACKING_ID === 'G-XXXXXXXXXX') {
    console.warn('Google Analytics tracking ID not configured');
    return;
  }

  analyticsConsent = hasAnalyticsConsent;

  // Initialize dataLayer first
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };

  // Set initial consent state
  window.gtag('consent', 'default', {
    analytics_storage: hasAnalyticsConsent ? 'granted' : 'denied',
    ad_storage: 'denied', // We'll handle this through cookie consent
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });

  // Load Google Analytics script dynamically
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
  document.head.appendChild(script);

  // Configure GA
  window.gtag('js', new Date());
  window.gtag('config', GA_TRACKING_ID, {
    page_title: document.title,
    page_location: window.location.href,
    send_page_view: hasAnalyticsConsent,
    anonymize_ip: true, // GDPR compliance
    allow_google_signals: hasAnalyticsConsent,
    allow_ad_personalization_signals: false, // Disable by default
  });
};

export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  if (typeof window === 'undefined' || !window.gtag || !analyticsConsent) {
    if (!analyticsConsent) {
      console.log('Analytics tracking skipped - no consent');
    } else {
      console.warn('Google Analytics not initialized');
    }
    return;
  }

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });
};

export const trackPageView = (page_path: string, page_title?: string) => {
  if (typeof window === 'undefined' || !window.gtag || !analyticsConsent) {
    if (!analyticsConsent) {
      console.log('Page view tracking skipped - no consent');
    } else {
      console.warn('Google Analytics not initialized');
    }
    return;
  }

  window.gtag('config', GA_TRACKING_ID, {
    page_path,
    page_title: page_title || document.title,
  });
};

// Specific tracking functions for common events
export const trackButtonClick = (buttonName: string, location?: string) => {
  trackEvent('click', 'button', `${buttonName}${location ? ` - ${location}` : ''}`, 1);
};

export const trackFormSubmission = (formName: string, success: boolean = true) => {
  trackEvent(success ? 'submit' : 'submit_error', 'form', formName, success ? 1 : 0);
};

export const trackSocialShare = (platform: string, content?: string) => {
  trackEvent('share', 'social', `${platform}${content ? ` - ${content}` : ''}`, 1);
};

export const trackWaitlistSignup = (email: string) => {
  trackEvent('signup', 'waitlist', 'email_signup', 1);
  // Track conversion event
  window.gtag('event', 'conversion', {
    send_to: GA_TRACKING_ID,
    event_category: 'engagement',
    event_label: 'waitlist_signup'
  });
};

export const trackNavigation = (destination: string, source?: string) => {
  trackEvent('navigate', 'navigation', `${destination}${source ? ` from ${source}` : ''}`, 1);
};

export const trackDownload = (fileName: string, fileType?: string) => {
  trackEvent('download', 'file', `${fileName}${fileType ? ` - ${fileType}` : ''}`, 1);
};

export const trackContactForm = (formType: string, success: boolean = true) => {
  trackEvent(success ? 'submit' : 'submit_error', 'contact', formType, success ? 1 : 0);
};

export const trackUserInteraction = (interactionType: string, details?: string) => {
  trackEvent('interaction', 'user', `${interactionType}${details ? ` - ${details}` : ''}`, 1);
};